import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { VariantWithStockDTO } from "@/modules/inventory/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { RowSelectionState } from "@tanstack/react-table";
import { useCallback, useState } from "react";
import { inventoryApi } from "../api/client";
import { useStockListQuery, useStockSummaryQuery } from "../api/queries";
import {
  buildStockTemplateCsv,
  downloadCsv,
  stockTemplateFilename,
} from "../lib/stockCsv";
import { StockAdjustDialog } from "../ui/StockAdjustDialog";
import { StockCsvImportDialog } from "../ui/StockCsvImportDialog";
import { StockFilter } from "../ui/StockFilter";
import { StockTable } from "../ui/StockTable";
import { StockSummaryCards } from "../ui/StockSummaryCards";
import { StockToolbar } from "../ui/StockToolbar";

/** เลือกครบทุกแถวในหน้าปัจจุบัน (หัวตาราง) — ใช้ index ตาม DataTable */
function isAllRowsOnCurrentPageSelected(
  rowSelection: RowSelectionState,
  pageRowCount: number,
): boolean {
  if (pageRowCount === 0) return false;
  for (let i = 0; i < pageRowCount; i++) {
    if (rowSelection[String(i)] !== true) return false;
  }
  return true;
}

export function StockListPage() {
  const nav = useNavigate({ from: "/app/inventory/stock" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/inventory/stock",
  });
  const list = useStockListQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const summary = useStockSummaryQuery(search.filters);
  const canManage = useActionPermission(["inventory:update"]);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] =
    useState<VariantWithStockDTO | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportTemplateLoading, setExportTemplateLoading] = useState(false);

  const pageData = list.data?.data ?? [];
  const totalCount = list.data?.meta?.total ?? 0;

  const handleAdjustStock = (variant?: VariantWithStockDTO) => {
    setSelectedVariant(variant ?? null);
    setAdjustDialogOpen(true);
  };

  const handleViewMovements = (variant: VariantWithStockDTO) => {
    nav({
      to: "/app/inventory/movements",
      search: { variantId: variant.variantId },
    });
  };

  const handleExportTemplate = useCallback(async () => {
    const selectedKeys = Object.keys(rowSelection).filter(
      (k) => rowSelection[k],
    );
    const allRowsOnPageSelected = isAllRowsOnCurrentPageSelected(
      rowSelection,
      pageData.length,
    );

    let rowsToExport: VariantWithStockDTO[] = [];

    if (selectedKeys.length > 0 && !allRowsOnPageSelected) {
      rowsToExport = pageData.filter(
        (_, i) => rowSelection[String(i)] === true,
      );
    } else {
      if (totalCount === 0) return;
      setExportTemplateLoading(true);
      try {
        rowsToExport = await inventoryApi.stock.listAllPages(
          { sort: search.sort, filters: search.filters },
          totalCount,
        );
      } finally {
        setExportTemplateLoading(false);
      }
    }

    if (rowsToExport.length === 0) return;
    const csv = buildStockTemplateCsv(rowsToExport);
    downloadCsv(csv, stockTemplateFilename());
  }, [
    pageData,
    rowSelection,
    totalCount,
    search.sort,
    search.filters,
  ]);

  const handlePaginationChange = useCallback(
    (offset: number, limit: number) => {
      const currentOffset = search.offset ?? 0;
      const currentLimit = search.limit ?? 20;
      if (offset === currentOffset && limit === currentLimit) return;
      nav({ search: { ...search, offset, limit } });
    },
    [nav, search],
  );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ສະຕ໋ອກ" },
          ]}
        />
      </Header>
      <Main>
        <StockToolbar
          canManage={!!canManage}
          onAdjustStock={() => handleAdjustStock()}
          onStartTour={() => {
            // TODO: Start tour
            console.log("Start tour");
          }}
          onExportTemplate={canManage ? handleExportTemplate : undefined}
          onImportCsv={canManage ? () => setImportDialogOpen(true) : undefined}
          canExportTemplate={canManage && totalCount > 0}
          exportTemplateLoading={exportTemplateLoading}
        />
        <StockSummaryCards
          data={summary.data}
          isLoading={summary.isLoading}
        />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <StockFilter />
          <StockTable
            canManage={!!canManage}
            data={pageData}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={totalCount}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={handlePaginationChange}
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onRowSelectionChange={canManage ? setRowSelection : undefined}
            enableRowSelection={!!canManage}
            onAdjustStock={handleAdjustStock}
            onViewMovements={handleViewMovements}
          />
        </div>
      </Main>
      <StockAdjustDialog
        open={adjustDialogOpen}
        onOpenChange={setAdjustDialogOpen}
        variant={selectedVariant}
        onSuccess={() => {
          list.refetch();
        }}
      />
      <StockCsvImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={() => {
          list.refetch();
        }}
      />
    </>
  );
}
